import React, { useCallback, useMemo } from 'react';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Checkbox } from 'apps/sigma2-ui/src/components/ui/Checkbox';
import { useCrudOperations } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/use-crud-operations';
import { COPILOT_MODEL_DEFAULT_ALIAS } from 'libs/end-to-end/src/types/databases/scheduling/copilot-model-by-job-title-config';
import { CopilotJobTitleModelEntry } from 'libs/end-to-end/src/types/databases/scheduling/copilot-model-by-job-title-config';
import { CopilotModelByJobTitleConfig } from 'libs/end-to-end/src/types/databases/scheduling/copilot-model-by-job-title-config';
import { Autocomplete } from '@mui/material';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { TextField } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const newEntry = (): CopilotJobTitleModelEntry => ({
  jobTitleOrJobTitleGroup: '',
  modelName: '',
  alias: COPILOT_MODEL_DEFAULT_ALIAS,
});

export interface CopilotModelByJobTitleCopy {
  cardTitle: string;
  toggleLabel: string;
  emptyStateText: string;
  rowLabel: string;
  rowHelperText: string;
}

const GENERIC_COPY_BY_JOB_TITLE_MODE = {
  jobTitle: {
    cardTitleSuffix: 'model by job title',
    toggleLabel: 'Enable model selection by job title',
    emptyStateText: 'No entries. Add a row to assign a model per job title.',
    rowLabel: 'Job title',
    rowHelperText: 'Job titles are sourced from the transformed data',
  },
  jobTitleGroup: {
    cardTitleSuffix: 'model by job title group',
    toggleLabel: 'Enable model selection by job title group',
    emptyStateText:
      'No entries. Add a row to assign a model per job title group.',
    rowLabel: 'Job title group',
    rowHelperText:
      'Job title groups are sourced from the transformed data. Job title groups take precedence over job titles.',
  },
} as const;

export const buildCopilotModelByJobTitleCopy = (
  copilotDisplayName: string,
  useJobTitleGroups: boolean,
): CopilotModelByJobTitleCopy => {
  const generic = useJobTitleGroups
    ? GENERIC_COPY_BY_JOB_TITLE_MODE.jobTitleGroup
    : GENERIC_COPY_BY_JOB_TITLE_MODE.jobTitle;

  return {
    cardTitle: `${copilotDisplayName} Copilot: ${generic.cardTitleSuffix}`,
    toggleLabel: generic.toggleLabel,
    emptyStateText: generic.emptyStateText,
    rowLabel: generic.rowLabel,
    rowHelperText: generic.rowHelperText,
  };
};

export interface ConfigureCopilotModelByJobTitleProps {
  /** Copilot type label, e.g. "Hiring" or "Promotion". */
  copilotDisplayName: string;
  jobTitleSuggestions: string[];
  details: CopilotModelByJobTitleConfig;
  updateDetails: (config: CopilotModelByJobTitleConfig) => void;
  /** When true, persisted on config updates and selects job-title-group copy. */
  useJobTitleGroups: boolean;
}

export const ConfigureCopilotModelByJobTitle = ({
  copilotDisplayName,
  jobTitleSuggestions,
  details,
  updateDetails,
  useJobTitleGroups,
}: ConfigureCopilotModelByJobTitleProps) => {
  const copy = useMemo(
    () =>
      buildCopilotModelByJobTitleCopy(copilotDisplayName, useJobTitleGroups),
    [copilotDisplayName, useJobTitleGroups],
  );
  const isEnabled = details?.usePretrainedModel ?? false;
  const toggleEnabled = useCallback(() => {
    if (isEnabled) {
      updateDetails({
        usePretrainedModel: false,
        useJobTitleGroups,
        modelByJobTitle: details.modelByJobTitle,
      });
    } else {
      updateDetails({
        usePretrainedModel: true,
        useJobTitleGroups,
        modelByJobTitle: details.modelByJobTitle,
      });
    }
  }, [isEnabled, updateDetails, details.modelByJobTitle, useJobTitleGroups]);

  const items = details?.modelByJobTitle ?? [];
  const entriesSummary =
    items.length === 0
      ? 'No entries'
      : `${items.length} ${items.length === 1 ? 'entry' : 'entries'}`;

  const setItems = useCallback(
    (newItems: CopilotJobTitleModelEntry[]) => {
      updateDetails({
        usePretrainedModel: isEnabled,
        useJobTitleGroups,
        modelByJobTitle: newItems,
      });
    },
    [updateDetails, isEnabled, useJobTitleGroups],
  );

  const crud = useCrudOperations({
    items,
    setItems,
  });

  const handleAdd = useCallback(() => {
    crud.onAdd(newEntry());
  }, [crud]);

  return (
    <div className="space-y-6 p-6 bg-white rounded-lg shadow-md">
      <h3 className="text-lg font-semibold">{copy.cardTitle}</h3>
      <S2Checkbox checked={isEnabled} onChange={toggleEnabled}>
        {copy.toggleLabel}
      </S2Checkbox>
      {isEnabled && (
        <Accordion defaultExpanded elevation={0} disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} className="px-0">
            <span className="text-gray-700">Configure ({entriesSummary})</span>
          </AccordionSummary>
          <AccordionDetails className="pt-0">
            <Spacing size={'md'} direction={'vertical'}>
              <div className="flex justify-between items-center">
                <Button
                  behavior={{ type: 'onClick', onClick: handleAdd }}
                  text="+ Add row"
                  size="small"
                  variant="primary"
                />
              </div>
              {items.length === 0 && (
                <div className="text-gray-500 italic">
                  {copy.emptyStateText}
                </div>
              )}
              {items.map((entry, index) => (
                <Box key={index} display="flex" flexDirection="col" gap="xs">
                  <Box
                    display="flex"
                    flexDirection="row"
                    flexWrap="wrap"
                    alignItems="start"
                    gap="xs"
                  >
                    <Autocomplete
                      freeSolo
                      options={jobTitleSuggestions}
                      value={entry.jobTitleOrJobTitleGroup}
                      onChange={(_event, value) => {
                        crud.onEdit(index, {
                          jobTitleOrJobTitleGroup:
                            typeof value === 'string' ? value : (value ?? ''),
                        });
                      }}
                      onInputChange={(_event, value) => {
                        crud.onEdit(index, {
                          jobTitleOrJobTitleGroup: value,
                        });
                      }}
                      sx={{ flex: 1, minWidth: 200 }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={copy.rowLabel}
                          size="small"
                          helperText={copy.rowHelperText}
                        />
                      )}
                    />
                    <TextField
                      label="Tag"
                      size="small"
                      value={entry.alias ?? COPILOT_MODEL_DEFAULT_ALIAS}
                      onChange={(e) =>
                        crud.onEdit(index, { alias: e.target.value })
                      }
                      sx={{ minWidth: 100 }}
                      helperText="MLflow version alias (e.g. prod)"
                    />
                    <IconButton
                      aria-label="Remove row"
                      onClick={() => crud.onDelete(index)}
                      size="small"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <TextField
                    label="Model name"
                    size="small"
                    fullWidth
                    value={entry.modelName ?? ''}
                    onChange={(e) =>
                      crud.onEdit(index, { modelName: e.target.value })
                    }
                    helperText="Full MLflow registered model name"
                  />
                </Box>
              ))}
            </Spacing>
          </AccordionDetails>
        </Accordion>
      )}
    </div>
  );
};
